import { FieldGroupRowStatus } from "../../../models/intake";

/**
 * One field's specialist-reviewed correction as built client-side for the
 * Confirm-All bulk commit (Spec #34102 / F1d, AC4). Field names match the
 * backend's IntakeController.FieldCorrectionDto shape 1:1 (case-sensitive -
 * resolved server-side via IAnalysisFieldMapper.GetFieldIdentifierAsync).
 */
export interface QuickEntryCorrectionPayload {
    readonly fieldName: string;
    readonly aiValue: string | null;
    readonly specialistValue: string | null;
    readonly confidenceStateAtEdit: string;
    readonly documentType: string | null;
    /**
     * AB#38683. Whether the specialist kept the AI's value (true) or replaced it
     * (false); null when there is no judgement to record - the row was never
     * reviewed, or the AI extracted nothing for it.
     *
     * This is sent because the CLIENT is the only place that can answer it. The
     * stored procedure used to re-derive it by comparing the specialist's value
     * against the stored extraction, and that comparison is between two different
     * formats - a display string against a raw one - so it read "changed" for
     * hundreds of fields the specialist had simply accepted. Ali Moll, PR !10280.
     */
    readonly valueAccepted: boolean | null;
}

/**
 * Per-row input to {@link buildQuickEntryCorrection}. Deliberately separate
 * from FieldGroupRow (models/intake.ts, owned by F1a): FieldGroupRow's
 * `value` alone cannot distinguish "explicitly cleared to blank" (Business
 * Rule 10) from "never reviewed" (Business Rule 9) - both would otherwise
 * read as `value: null`. `explicitlyCleared` carries that extra bit F1d
 * needs without widening the shared F1a contract.
 */
export interface QuickEntryCorrectionInput {
    readonly fieldName: string;
    /** The AI's original extracted value for this field, or null if AI never populated it (e.g. rows with no AI wiring yet). */
    readonly aiValue: string | null;
    readonly status: FieldGroupRowStatus;
    /** The row's current displayed value (specialist-entered or AI-accepted), or null while not yet Confirmed. */
    readonly value: string | null;
    /** True when the specialist explicitly cleared this field's value to blank (Business Rule 10), as opposed to never having reviewed it (Business Rule 9). */
    readonly explicitlyCleared: boolean;
    /**
     * AB#38739 fallout (AB#38683): the specialist has SETTLED this row by acknowledging it. Amber is
     * acknowledge-satisfiable, and since AB#38739 the Request Type/Purpose row can never reach
     * Confirmed - so `status === Confirmed` stopped being able to say "reviewed" for exactly the two
     * fields Feature 2 measures. Optional so the existing call sites and tests are untouched;
     * undefined reads as not settled, which is the pre-AB#38739 behaviour.
     */
    readonly settled?: boolean;
    readonly documentType: string | null;
}

/**
 * Whether two values are the same answer. Case- and whitespace-insensitive only:
 * anything more (date parsing, list re-ordering, code-to-label mapping) belongs
 * upstream where the field's type is known, not in a generic comparison that
 * would have to guess. Callers are expected to hand this function two values
 * already in the same form - see getQuickEntryCorrectionPayloads.
 */
export function isSameCorrectionValue(left: string | null, right: string | null): boolean {
    const norm = (v: string | null) => (v ?? "").trim().replace(/\s+/g, " ").toLowerCase();
    return norm(left) === norm(right);
}

/**
 * Maps one row's current state to its Confirm-All correction payload
 * (Business Rules 7-10, Spec #34102 / F1d):
 *   - Rule 7 (edited): Confirmed, value !== aiValue -> specialistValue = value.
 *   - Rule 8 (accepted, unedited): Confirmed, value === aiValue -> specialistValue = aiValue.
 *   - Rule 9 (defensive, never reviewed): not Confirmed, not explicitly cleared -> specialistValue = null.
 *   - Rule 10 (explicit clear-to-blank): not Confirmed, explicitly cleared -> specialistValue = "" (distinct from null).
 *
 * and reports that same Rule 7-vs-Rule 8 decision explicitly as `valueAccepted`
 * (AB#38683), so no consumer has to re-derive it from the values.
 */
export function buildQuickEntryCorrection(input: QuickEntryCorrectionInput): QuickEntryCorrectionPayload {
    // AB#38739 fallout (AB#38683): Confirmed OR settled-by-acknowledgement. Both mean the specialist
    // looked at the row and let it stand; only the tier differs, and the tier is deliberately amber
    // for Request Type/Purpose now (AB#37979: it must not read as AI-confirmed). confidenceStateAtEdit
    // below still records the real status, so what the specialist SAW is not falsified - it is only
    // the accepted-vs-edited judgement that needs the wider definition of "reviewed".
    const reviewed = input.status === FieldGroupRowStatus.Confirmed || input.settled === true;
    // An AI value of null or blank means the AI produced nothing for this field, so
    // there is nothing to accept or reject however the specialist answered.
    const aiOffered = (input.aiValue ?? "").trim() !== "";
    const accepted = reviewed && aiOffered && isSameCorrectionValue(input.value, input.aiValue);

    let specialistValue: string | null;
    if (!reviewed) {
        specialistValue = input.explicitlyCleared ? "" : null;
    } else if (accepted) {
        specialistValue = input.aiValue ?? null;
    } else {
        specialistValue = input.value;
    }

    return {
        fieldName: input.fieldName,
        aiValue: input.aiValue,
        specialistValue,
        valueAccepted: !reviewed || !aiOffered ? null : accepted,
        // Reverse lookup on the numeric FieldGroupRowStatus enum - pass-through
        // string mirror of the frontend's status, no new backend enum (Technical Design).
        confidenceStateAtEdit: FieldGroupRowStatus[input.status],
        documentType: input.documentType,
    };
}

/** Maps a full set of row inputs to the Confirm-All bulk-commit payload (AC4). */
export function buildQuickEntryCorrections(inputs: readonly QuickEntryCorrectionInput[]): readonly QuickEntryCorrectionPayload[] {
    return inputs.map(buildQuickEntryCorrection);
}
